import { useState } from 'react';
import { flushSync } from 'react-dom';
import { RangeSlider, Loader } from 'ui';
import { useTranslation } from '@/i18n/client';
import { useProductList } from '@/modules/product/context/productList';
import { fetchBrandsFilterList } from '@/modules/brands/api';
import { fetchFilterList } from '@/modules/product/api/productList';
import { formatFiltersBeforeSend } from '../filterBar/utils';

import styles from './RangeFilter.module.scss';
import { RangeFilterProps } from './types';

export const RangeFilter = ({
	from,
	slug,
	sidebarFilters,
	selectedValues,
	to,
	setSidebarFilters,
	setSidebarFilteredFilterList,
	suffix,
}: RangeFilterProps) => {
	const { t } = useTranslation(['common']);
	const { categorySlug, brandSlug } = useProductList();
	const [isLoading, setIsLoading] = useState(false);

	const handleRangeChange = (min: number, max: number) => {
		const newSidebarFiltersValue = [...selectedValues] as string[];

		if (min * 100 === Number(from) && max * 100 === Number(to)) {
			newSidebarFiltersValue.length = 0;
		} else {
			if (newSidebarFiltersValue.length === 0) {
				newSidebarFiltersValue.push(`${min} ${suffix}`);
				newSidebarFiltersValue.push(`${max} ${suffix}`);
			} else {
				newSidebarFiltersValue[0] = `${min} ${suffix}`;
				newSidebarFiltersValue[1] = `${max} ${suffix}`;
			}
		}

		flushSync(() => {
			setSidebarFilters((oldValue) => {
				const newValue = [...oldValue];

				const filterIndex = newValue.findIndex((filter) => filter.slug === slug);

				newValue[filterIndex].selectedValue = newSidebarFiltersValue;

				return newValue;
			});
		});
	};

	const handleRangeBlur = async (min: number, max: number) => {
		setIsLoading(true);

		const newSidebarFiltersValue = [...selectedValues] as string[];

		if (min * 100 === Number(from) && max * 100 === Number(to)) {
			newSidebarFiltersValue.length = 0;
		} else {
			if (newSidebarFiltersValue.length === 0) {
				newSidebarFiltersValue.push(`${min} ${suffix}`);
				newSidebarFiltersValue.push(`${max} ${suffix}`);
			} else {
				newSidebarFiltersValue[0] = `${min} ${suffix}`;
				newSidebarFiltersValue[1] = `${max} ${suffix}`;
			}
		}

		const newSidebarFilters = [...sidebarFilters];

		const filterIndex = newSidebarFilters.findIndex((filter) => filter.slug === slug);

		newSidebarFilters[filterIndex].selectedValue = newSidebarFiltersValue;

		const newFilters = formatFiltersBeforeSend([...newSidebarFilters]);

		const response = brandSlug
			? await fetchBrandsFilterList(brandSlug, newFilters)
			: await fetchFilterList(categorySlug, newFilters);

		setSidebarFilteredFilterList(response);
		setIsLoading(false);
	};

	return (
		<>
			<div className={styles.rangeFilter}>
				<RangeSlider
					min={Number(from) / 100}
					minVal={
						selectedValues[0]
							? Number(from) / 100 > Number(selectedValues[0].split(' ')[0])
								? Number(from) / 100
								: Number(selectedValues[0].split(' ')[0])
							: undefined
					}
					max={Number(to) / 100}
					maxVal={
						selectedValues[1]
							? Number(to) / 100 < Number(selectedValues[1].split(' ')[0])
								? Number(to) / 100
								: Number(selectedValues[1].split(' ')[0])
							: undefined
					}
					onBlur={({ min, max }) => handleRangeBlur(min, max)}
					onChange={({ min, max }) => handleRangeChange(min, max)}
					fromLabel={t('common.from') as string}
					toLabel={t('common.to') as string}
					suffix={suffix}
				/>
			</div>
			{isLoading && <Loader />}
		</>
	);
};
